import React, { useMemo } from "react";
import ProfileScreen from "../../screens/user/ProfileScreen";
import { MockedProvider } from "@apollo/client/testing/react";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { MockedAuthManager } from "../../contexts/AuthManager";
import {
    buildClient,
    buildClientProfile,
    buildIjmGroup,
    buildUser,
    buildWorkerProfile,
    IJM_CHOICES_MOCK,
} from "../factories";
import { Cct, EarningType, EducationLevel, ProfileType } from "../../__generated__/graphql";
import WorkerProfileEditorScreen from "../../screens/worker/WorkerProfileEditor";
import { useI18n } from "../../hooks/i18n-hooks";
import { UPDATE_CLIENT_PROFILE, UPDATE_WORKER_PROFILE } from "../../graphql/core";
import ClientProfileEditorScreen from "../../screens/client/ClientProfileEditor";
import BankAccountEditorScreen from "../../screens/profile-editor/BankAccountEditor";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import AccountEditorScreen from "../../screens/user/AccountEditor";
import IJMEditorNavScreen from "../../screens/client/IJMEditorNavScreen";
import ExperiencesEditorScreen from "../../screens/profile-editor/ExperiencesEditor";
import { StoryStackNavigatorContainer } from "../display-helpers";
import { MockLink } from "@apollo/client/testing";
import { Buildable } from "ts-essentials";
import { IjmSelection } from "../../utils";

const Stack = createNativeStackNavigator<{
    Profile: undefined;
    WorkerProfileEdit: undefined;
    DiplomaAndTrainingEdit: undefined;
    BankAccountEdit: undefined;
    ExperiencesEdit: undefined;
    ClientProfileEdit: undefined;
    AccountEdit: undefined;
    PrivateClientEdit: undefined;
    IJMEditorNavScreen: {
        ijmSelection?: Buildable<IjmSelection> | undefined;
        salaryInfo?:
            | {
                  amount: number;
                  earningType: EarningType;
              }
            | undefined;
        cct?: Cct;
        useLse: boolean;
    };
}>();

type FullProfileType = ProfileType.Worker | ProfileType.Client | "private_client";

const StoryComponent = ({ fullProfileType }: { fullProfileType: FullProfileType }) => {
    const i18n = useI18n();
    const [profileType, isPrivate] =
        fullProfileType === ProfileType.Worker
            ? [ProfileType.Worker, false]
            : fullProfileType === "private_client"
              ? [ProfileType.Client, true]
              : [ProfileType.Client, false];

    const user = useMemo(
        () =>
            buildUser({
                workerProfile:
                    profileType == ProfileType.Worker
                        ? buildWorkerProfile({
                              id: "worker",
                              educationLevel: EducationLevel.HesHep,
                          })
                        : undefined,
                clientProfiles:
                    profileType == ProfileType.Client
                        ? [
                              buildClientProfile({
                                  id: "client",
                                  isPrivateClient: isPrivate,
                                  client: buildClient({
                                      canChangeIjmGroup: true,
                                      ijmGroup: buildIjmGroup({
                                          id: "GM-G1-730-2",
                                          name: "2 jours de délai d'attente",
                                      }),
                                      ijmPayFirstDays: true,
                                      ijmPayRemainingPercents: 20,
                                  }),
                              }),
                          ]
                        : [],
            }),
        [isPrivate, profileType],
    );
    const mocks = useMemo<MockLink.MockedResponse[]>(
        () => [
            IJM_CHOICES_MOCK,
            {
                maxUsageCount: Number.POSITIVE_INFINITY,
                request: {
                    query: UPDATE_WORKER_PROFILE,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        updateWorkerProfile: buildWorkerProfile({
                            children: [],
                            hasFiscalAttestation: false,
                        }),
                    },
                }),
            },
            {
                request: {
                    query: UPDATE_CLIENT_PROFILE,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        updateClientProfile: buildClientProfile({}),
                    },
                }),
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType} profileId={profileType}>
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="Profile">
                            <Stack.Screen
                                name="Profile"
                                component={ProfileScreen}
                                options={{ headerShown: false }}
                            />
                            <Stack.Screen
                                name="WorkerProfileEdit"
                                component={WorkerProfileEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("user.actions.edit_user"),
                                }}
                            />
                            <Stack.Screen
                                name="ClientProfileEdit"
                                component={ClientProfileEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("user.actions.edit_user"),
                                }}
                            />
                            <Stack.Screen
                                name="BankAccountEdit"
                                component={BankAccountEditorScreen}
                            />
                            <Stack.Screen
                                name="ExperiencesEdit"
                                component={ExperiencesEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("screens.profile_editor.experiences.title"),
                                }}
                            />
                            <Stack.Screen
                                name="AccountEdit"
                                component={AccountEditorScreen}
                                options={{
                                    headerShown: true,
                                    title: i18n.t("screens.account_edit.title"),
                                }}
                            />
                            <Stack.Screen
                                name="IJMEditorNavScreen"
                                component={IJMEditorNavScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    animation: "slide_from_bottom",
                                }}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Profile screen",
    component: StoryComponent,
    argTypes: {
        fullProfileType: {
            options: [ProfileType.Worker, ProfileType.Client, "private_client"],
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        fullProfileType: ProfileType.Worker,
    },
};

export const Client: Story = {
    args: {
        fullProfileType: ProfileType.Client,
    },
};

export const PrivateClient: Story = {
    args: {
        fullProfileType: "private_client",
    },
};
